// ABOUTME: Shop management section with cards for viewing, adding, and editing shops.
// ABOUTME: Provides shop CRUD operations within the configuration page.

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';
import ShopEditDialog from './ShopEditDialog';

export default function ShopManagementSection({ shops, onSaveShop, onDeleteShop, categories = [] }) {
  const { t } = useTranslation();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingShop, setEditingShop] = useState(null);

  // Only enabled real shops can be members of a virtual shop
  const candidateShops = (shops || []).filter(shop => !shop.virtual && shop.enabled !== false);

  const handleEdit = (shop) => {
    setEditingShop(shop);
    setEditDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingShop(null);
    setEditDialogOpen(true);
  };

  const handleSave = (shopData) => {
    onSaveShop(shopData);
    setEditDialogOpen(false);
    setEditingShop(null);
  };

  const handleDelete = (shopId) => {
    onDeleteShop(shopId);
    setEditDialogOpen(false);
    setEditingShop(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
          {t('config.tabs.shops')}
        </Typography>
        <Button
          variant="outlined"
          onClick={handleAdd}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: colors.border.light,
            color: colors.text.primary,
            fontWeight: 500,
          }}
        >
          + {t('config.shop.addShop')}
        </Button>
      </Box>

      <Grid container spacing={2}>
        {(shops || []).map(shop => (
          <Grid item xs={12} sm={6} md={4} key={shop.shop_id}>
            <Card sx={{
              borderRadius: '12px',
              border: `1px solid ${colors.border.light}`,
              boxShadow: 'none',
              '&:hover': { borderColor: colors.palette.orange },
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
                      {shop.name}
                    </Typography>
                    {shop.virtual && (
                      <Chip
                        label={t('common.virtualShop')}
                        size="small"
                        sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 500, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                  <Chip
                    label={shop.enabled ? t('config.shop.enabled') : t('config.shop.disabled')}
                    size="small"
                    sx={{
                      backgroundColor: shop.enabled ? colors.status.success + '20' : colors.border.light,
                      color: shop.enabled ? colors.status.success : colors.text.muted,
                      fontWeight: 500,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>

                <Typography sx={{ fontSize: '0.8rem', color: colors.text.muted, mb: 0.5 }}>
                  ID: {shop.shop_id}
                </Typography>
                {shop.address && (
                  <Typography sx={{ fontSize: '0.8rem', color: colors.text.secondary, mb: 0.5 }}>
                    {shop.address}
                  </Typography>
                )}
                {shop.virtual ? (
                  <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted, mb: 1 }}>
                    {t('common.memberCount', { n: shop.members?.length || 0 })}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted, mb: 1 }}>
                    DB: {shop.db_name}
                  </Typography>
                )}

                <Button
                  size="small"
                  onClick={() => handleEdit(shop)}
                  sx={{
                    textTransform: 'none',
                    color: colors.ui.button,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                  }}
                >
                  {t('config.shop.editShop')}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ShopEditDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingShop(null); }}
        onSave={handleSave}
        onDelete={handleDelete}
        shop={editingShop}
        candidateShops={candidateShops}
        categories={categories}
      />
    </Box>
  );
}
